"use client";

import { useEffect, useRef } from "react";
import {
  FolderTreeItem,
  Zap,
  useStorageZap,
  HydratedZapAsset,
} from "@zapier/client";
import { useNotification, normalizeZapNodeId } from "@zapier/utils";
import { FolderModal } from "../../types";
import { ZapModal } from "../../components/zaps/types";
import useRouterParams from "../useRouterParams/useRouterParams";

const ORIENTATION_DELAY = 500; // 500ms delay for user orientation

interface UseModalUrlParametersOptions {
  setCurrentFolderModal: (modal: FolderModal | undefined) => void;
  setCurrentZapModal: (modal: ZapModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
  setCurrentZap: (zap: Zap | undefined) => void;
  folder?: FolderTreeItem;
  zaps?: Zap[] | HydratedZapAsset[];
  isLoading?: boolean;
}

export function useModalUrlParameters({
  setCurrentFolderModal,
  setCurrentZapModal,
  setCurrentFolder,
  setCurrentZap,
  folder,
  zaps = [],
  isLoading = false,
}: UseModalUrlParametersOptions) {
  const { modal, zapId: rawZapId, setParams } = useRouterParams();
  const { createNotification } = useNotification();
  const hasProcessedUrlModal = useRef(false);

  // Extract the actual zap ID from either CUID or regular node ID
  const zapId = normalizeZapNodeId(rawZapId);

  const { zap: fetchedZap, isLoading: isZapLoading } = useStorageZap({
    id: zapId ? Number(zapId) : undefined,
    enabled: !!zapId && modal === "move",
  });

  useEffect(() => {
    if (isLoading || isZapLoading || hasProcessedUrlModal.current || !modal) {
      return;
    }

    const clearModalParameter = () => {
      setParams({}, ["modal", "zap_id"]);
    };

    const orientationTimeout = setTimeout(() => {
      hasProcessedUrlModal.current = true;

      if (modal === "share") {
        if (folder) {
          if (folder.parentId === undefined) {
            setCurrentFolder(folder);
            setCurrentFolderModal(FolderModal.Share);
          } else {
            createNotification({
              message:
                "Unable to share this folder. Only root folders can be shared.",
              type: "error",
            });
          }
          clearModalParameter();
        }
      } else if (modal === "move") {
        if (zapId) {
          if (
            (fetchedZap &&
              String(fetchedZap.folderId) === String(folder?.id)) ||
            //allow launching move modal for zaps in the personal folder
            (!folder && !fetchedZap?.folderId)
          ) {
            const zapInList = zaps.find(
              (zap) => String(zap.id) === String(zapId),
            );

            setCurrentZap((zapInList as Zap) || (fetchedZap as Zap));
            setCurrentZapModal(ZapModal.Move);
          } else {
            createNotification({
              message:
                "Unable to move Zap. The specified Zap was not found in this folder.",
              type: "error",
            });
          }
          clearModalParameter();
        } else if (folder) {
          setCurrentFolder(folder);
          setCurrentFolderModal(FolderModal.Move);
          clearModalParameter();
        }
      }
    }, ORIENTATION_DELAY);

    return () => {
      clearTimeout(orientationTimeout);
    };
  }, [
    modal,
    zapId,
    folder,
    zaps,
    isLoading,
    isZapLoading,
    fetchedZap,
    setCurrentFolderModal,
    setCurrentZapModal,
    setCurrentFolder,
    setCurrentZap,
    setParams,
    createNotification,
  ]);

  useEffect(() => {
    hasProcessedUrlModal.current = false;
  }, [folder?.id]);

  const closeModalAndClearParameters = () => {
    setCurrentFolderModal(undefined);
    setCurrentZapModal(undefined);
    setCurrentFolder(undefined);
    setCurrentZap(undefined);
    setParams({}, ["modal", "zap_id"]);
  };

  return {
    closeModalAndClearParameters,
  };
}
